import { cx } from '../../lib/cx';
import { SYMBOL_GO, WORDMARK, WORDMARK_NAME, WORDMARK_WIDTH } from './paths';
import type { BrandLockupProps, BrandLogotypeProps, BrandSymbolProps, BrandVariant } from './types';

const INK: Record<BrandVariant, string> = {
  light: '#0a0a0a',
  dark: '#fafafa',
  mono: 'currentColor',
};

const WORDMARK_RATIO = WORDMARK_WIDTH / 40;

const accessibility = (title: string, decorative: boolean) =>
  decorative ? { 'aria-hidden': true as const } : { role: 'img' as const, 'aria-label': title };

/**
 * The symbol: the "GO" of the wordmark, its O holding the forward arrow, white on a black tile.
 * Legible from 16 px; the same drawing is the app icon and the favicon (`packages/ui/assets/brand`).
 */
export const BrandSymbol = ({
  size = 32,
  title = WORDMARK_NAME,
  decorative = false,
  className,
}: BrandSymbolProps) => (
  <svg
    viewBox="0 0 64 64"
    width={size}
    height={size}
    className={className}
    {...accessibility(title, decorative)}
  >
    <rect width="64" height="64" rx="14" fill="#0a0a0a" />
    <path d={SYMBOL_GO} fill="#ffffff" />
  </svg>
);

/**
 * The wordmark: "PlantelGO" in Outfit Bold, the O of GO holding a forward arrow. One ink per
 * variant; outlines, so it renders the same everywhere without loading a font.
 */
export const BrandLogotype = ({
  height = 24,
  variant = 'light',
  title = WORDMARK_NAME,
  decorative = false,
  className,
}: BrandLogotypeProps) => (
  <svg
    viewBox={`0 0 ${WORDMARK_WIDTH} 40`}
    height={height}
    width={Math.round(height * WORDMARK_RATIO)}
    className={className}
    {...accessibility(title, decorative)}
  >
    <path d={WORDMARK} fill={INK[variant]} />
  </svg>
);

/**
 * The form the shell and the landing use. The wordmark already carries the GO with its arrow, so
 * the lockup is the wordmark at 80 % of the given height (the symbol is for square spots: favicon,
 * app icon, avatars). One accessible image.
 */
export const BrandLockup = ({
  height = 28,
  variant = 'light',
  title = WORDMARK_NAME,
  decorative = false,
  className,
}: BrandLockupProps) => (
  <span
    className={cx('inline-flex items-center align-middle', className)}
    {...accessibility(title, decorative)}
  >
    <BrandLogotype height={Math.round(height * 0.8)} variant={variant} decorative />
  </span>
);
